import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Users, Star } from "lucide-react";
import ManagerSchedulePanel from "./ManagerSchedulePanel";

const statusColors = {
  online: "bg-emerald-50 text-emerald-700 border-emerald-200",
  offline: "bg-zinc-100 text-zinc-600 border-zinc-200",
  busy: "bg-amber-50 text-amber-700 border-amber-200",
  unavailable: "bg-rose-50 text-rose-700 border-rose-200",
};

const approvalColors = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  on_hold: "bg-rose-50 text-rose-700 border-rose-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

const approvalLabels = {
  approved: "Approved",
  pending: "Pending",
  on_hold: "On Hold",
  rejected: "Rejected",
};

export default function VendorDirectory() {
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterApproval, setFilterApproval] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const fetchVendors = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterApproval !== "all") params.set("approval", filterApproval);
      if (filterStatus !== "all") params.set("status", filterStatus);
      params.set("limit", "50");
      const { data } = await API.get(`/management/vendors?${params}`);
      setVendors(data.vendors);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVendors(); }, [search, filterApproval, filterStatus]);

  const viewDetail = async (vendorId) => {
    try {
      const { data } = await API.get(`/management/vendors/${vendorId}`);
      setDetail(data);
      setSelected(vendorId);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-4" data-testid="technician-directory">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900">Technicians</h1>
          <p className="text-sm text-zinc-500">{total} technicians total</p>
        </div>
      </div>

      <Card className="border border-zinc-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input placeholder="Search technicians..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9" data-testid="technician-search-input" />
            </div>
            <Select value={filterApproval} onValueChange={setFilterApproval}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-approval">
                <SelectValue placeholder="Approval" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Approvals</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
                <SelectItem value="busy">Busy</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-zinc-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Technician</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Approval</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Active Jobs</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Coverage</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Rating</TableHead>
                <TableHead className="text-xs">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-400">Loading...</TableCell></TableRow>
              ) : vendors.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-400">No technicians found</TableCell></TableRow>
              ) : vendors.map(v => (
                <TableRow key={v.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => viewDetail(v.id)} data-testid={`technician-row-${v.id}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{v.full_name || "\u2014"}</p>
                      <p className="text-xs text-zinc-400">{v.company_name} &middot; {v.vendor_code}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusColors[v.current_status] || statusColors.offline}`}>
                      {v.current_status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${approvalColors[v.approval_status] || ""}`}>
                      {approvalLabels[v.approval_status] || v.approval_status}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="text-sm">{v.active_jobs || 0}</span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="text-xs text-zinc-500">{(v.coverage_zips || []).join(", ") || "\u2014"}</span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      <span className="text-sm">{v.rating_average || "\u2014"}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="text-xs" data-testid={`view-technician-${v.id}`}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setDetail(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{detail?.full_name || "Technician Detail"}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-zinc-400 text-xs">Company</span><p className="font-medium">{detail.company_name}</p></div>
                <div><span className="text-zinc-400 text-xs">Code</span><p className="font-mono text-xs">{detail.vendor_code}</p></div>
                <div><span className="text-zinc-400 text-xs">Phone</span><p>{detail.phone_number || "\u2014"}</p></div>
                <div><span className="text-zinc-400 text-xs">Email</span><p>{detail.email_public || "\u2014"}</p></div>
                <div><span className="text-zinc-400 text-xs">Status</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusColors[detail.current_status] || ""}`}>
                    {detail.current_status}
                  </span>
                </div>
                <div><span className="text-zinc-400 text-xs">Approval</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${approvalColors[detail.approval_status] || ""}`}>
                    {approvalLabels[detail.approval_status] || detail.approval_status}
                  </span>
                </div>
              </div>
              {detail.stats && (
                <div>
                  <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Performance</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-zinc-50 p-2 rounded-lg text-center">
                      <p className="font-bold">{detail.stats.acceptance_rate}%</p>
                      <p className="text-[10px] text-zinc-500">Accept Rate</p>
                    </div>
                    <div className="bg-zinc-50 p-2 rounded-lg text-center">
                      <p className="font-bold">{detail.stats.completion_rate}%</p>
                      <p className="text-[10px] text-zinc-500">Complete Rate</p>
                    </div>
                    <div className="bg-zinc-50 p-2 rounded-lg text-center">
                      <p className="font-bold">{detail.stats.avg_response_time}s</p>
                      <p className="text-[10px] text-zinc-500">Avg Response</p>
                    </div>
                  </div>
                </div>
              )}
              {detail.business_bio && (
                <div>
                  <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">Bio</h4>
                  <p className="text-zinc-600">{detail.business_bio}</p>
                </div>
              )}

              <div className="border-t border-zinc-200 pt-3">
                <ManagerSchedulePanel vendorId={detail.id} vendorName={detail.full_name} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
